import { captureException, captureMessage } from "@sentry/nextjs";

import { isProduction } from "@/env";

function toError(input: unknown): Error {
  if (input instanceof Error) return input;
  if (typeof input === "string") return new Error(input);
  try {
    return new Error(JSON.stringify(input));
  } catch {
    return new Error("Unknown error");
  }
}

type SentryCaptureContext = Parameters<typeof captureException>[1];
type PlainDetails = Record<string, unknown>;

function isEventHintLike(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return (
    "mechanism" in obj ||
    "originalException" in obj ||
    "syntheticException" in obj ||
    "event_id" in obj
  );
}

function isCaptureContextLike(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return (
    "user" in obj ||
    "level" in obj ||
    "extra" in obj ||
    "contexts" in obj ||
    "tags" in obj ||
    "fingerprint" in obj
  );
}

function toCaptureContext(
  input?: string | PlainDetails | SentryCaptureContext,
): SentryCaptureContext | undefined {
  if (!input) return undefined;
  if (typeof input === "string") return { extra: { message: input } };
  // If caller passed a Sentry-specific object, forward as-is
  if (isEventHintLike(input) || isCaptureContextLike(input)) return input;
  // Otherwise, treat as plain details and attach as `extra`
  return { extra: input as PlainDetails };
}

const sendMessage = (
  message: string,
  level: "warning" | "log",
  context?: string | PlainDetails | SentryCaptureContext,
) => {
  if (isProduction && level === "warning") {
    captureMessage(message, Object.assign({ level }, toCaptureContext(context)));
  } else if (!isProduction) {
    switch (level) {
      case "warning":
        console.warn(message, JSON.stringify(context, null, 2));
        break;
      case "log":
        console[level](message, JSON.stringify(context, null, 2));
        break;
    }
  }
};

export const Logger = {
  error: (error: unknown, context?: PlainDetails | SentryCaptureContext) => {
    const errorToSend = toError(error);
    if (isProduction) {
      captureException(errorToSend, toCaptureContext(context));
    } else {
      console.error(errorToSend, typeof context === "string" ? undefined : context);
    }
  },
  warn: (message: string, context?: PlainDetails | SentryCaptureContext) =>
    sendMessage(message, "warning", context),
  log: (message: string, context?: PlainDetails | SentryCaptureContext) =>
    sendMessage(message, "log", context),
};
